import { useEffect, useRef } from 'react';

/**
 * Bring an element into view the moment it appears.
 *
 * <p>Written for form errors. Every auth form on this site puts its refusal at
 * the TOP of the form -- above the fields, where a reader looking at the form
 * would find it -- and every one of those forms is long enough that the top is
 * off screen by the time somebody has filled the last box and pressed the
 * button. So the page answered, correctly and invisibly, and the reader saw a
 * button that appeared to do nothing. They press it again, which is the worst
 * possible reaction to a refusal that may have cost a Cognito call.
 *
 * <p><b>`block: 'center'` rather than `'start'`.</b> The nav bar is fixed once
 * the page has scrolled -- see themeNav.js -- so scrolling the error to the top
 * of the viewport puts it underneath the header, which is the same problem one
 * step along. Centre it and it is unambiguous at any header height.
 *
 * <p><b>Motion is a preference.</b> A smooth scroll is a nicety and an
 * accessibility hazard: somebody who has asked their system for less motion has
 * asked for a reason. The query is read at call time rather than at mount,
 * because the setting can change while a page is open.
 *
 * <p><b>jsdom does not implement scrollIntoView</b>, and calling it there is a
 * TypeError rather than a no-op -- which would turn every form-refusal test in
 * the jest suite red. Guarded by feature test, not by an environment check.
 *
 * @param pShown whether the element is on the page and worth scrolling to
 * @return a ref to put on the element
 */
export function useScrollIntoViewWhen( pShown ) {
    const aRef = useRef( null );

    useEffect( () => {
        const aNode = aRef.current;
        if ( !pShown || !aNode || typeof aNode.scrollIntoView !== 'function' ) return;

        const aStill = window.matchMedia
            && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

        aNode.scrollIntoView( { block: 'center', behavior: aStill ? 'auto' : 'smooth' } );
    }, [ pShown ] );

    return aRef;
}
